import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { SupportService, SupportTicket, SupportTicketDetails } from '../widgets/support/support.service';
import { LocalDatePipe } from '../shared/local-date.pipe';

@Component({
  selector: 'app-support-tickets-page',
  imports: [FormsModule, LocalDatePipe, RouterLink],
  template: `
    <section class="tickets-hero">
      <p class="eyebrow">Destek taleplerim</p>
      <h1>Konuşmalarını kaldığın yerden takip et.</h1>
      <p class="lead">Açılmış destek taleplerini görüntüle, cevapları oku ve aynı talep üzerinden yanıt ver.</p>
      <nav>
        <a [routerLink]="i18n.routePath('support')">Destek merkezi</a>
        <a [routerLink]="i18n.routePath('supportTickets')">Tüm talepler</a>
      </nav>
    </section>

    <section class="ticket-layout">
      <aside class="ticket-list">
        @if (tickets().length) {
          @for (ticket of tickets(); track ticket.id) {
            <a [routerLink]="i18n.routePath('supportTicketDetail', { slug: ticket.slug })" [class.active]="details()?.id === ticket.id">
              <strong>{{ ticket.title }}</strong>
              <span>{{ ticket.status }} · {{ ticket.priority }}</span>
            </a>
          }
        } @else {
          <p>Henüz destek talebin yok.</p>
        }
      </aside>

      <section class="ticket-detail">
        @if (details(); as ticket) {
          <header>
            <p class="eyebrow">#{{ ticket.id }}</p>
            <h2>{{ ticket.status }} · {{ ticket.priority }}</h2>
          </header>

          <div class="messages">
            @for (message of ticket.messages; track message.id) {
              <article [class.mine]="message.isYou">
                <strong>{{ message.senderLabel }}</strong>
                <p>{{ message.message }}</p>
                @if (message.createdAt) {
                  <small>{{ message.createdAt | localDate:'dateTime' }}</small>
                }
              </article>
            }
            @if (!ticket.messages.length) {
              <p class="empty">Bu talepte henüz mesaj yok.</p>
            }
          </div>

          <form (ngSubmit)="sendMessage(ticket)" class="reply-form">
            <textarea name="message" [(ngModel)]="message" rows="4" placeholder="Destek ekibine yanıt yaz"></textarea>
            <button type="submit" [disabled]="!message.trim() || sending()">Yanıt gönder</button>
          </form>
        } @else {
          <div class="empty-state">
            <h2>Bir destek talebi seç</h2>
            <p>Soldaki listeden bir talep seçtiğinde konuşma detayları burada açılır.</p>
          </div>
        }
      </section>
    </section>
  `,
  styles: [`
    .tickets-hero,
    .ticket-layout {
      margin: 0 auto;
      max-width: 1180px;
    }

    .tickets-hero {
      background: radial-gradient(circle at 20% 0%, var(--glow), transparent 34%), linear-gradient(135deg, var(--surface), color-mix(in srgb, var(--accent) 8%, var(--surface)));
      border: 1px solid var(--border);
      border-radius: 42px;
      box-shadow: 0 30px 80px var(--shadow);
      padding: clamp(34px, 7vw, 82px);
    }

    .tickets-hero nav {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 26px;
    }

    .tickets-hero a,
    .ticket-list,
    .ticket-detail {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 30px;
      box-shadow: 0 18px 46px color-mix(in srgb, var(--shadow) 56%, transparent);
    }

    .tickets-hero a {
      color: var(--ink);
      font-weight: 950;
      padding: 12px 16px;
      text-decoration: none;
    }

    .ticket-layout {
      display: grid;
      gap: 20px;
      grid-template-columns: 340px minmax(0, 1fr);
      margin-top: 22px;
    }

    .ticket-list,
    .ticket-detail {
      padding: 22px;
    }

    .ticket-list {
      align-self: start;
      display: grid;
      gap: 10px;
      position: sticky;
      top: 120px;
    }

    .ticket-list a {
      background: color-mix(in srgb, var(--paper) 52%, transparent);
      border: 1px solid var(--border);
      border-radius: 22px;
      color: var(--ink);
      display: grid;
      gap: 6px;
      padding: 14px;
      text-decoration: none;
    }

    .ticket-list a.active {
      border-color: var(--accent);
      box-shadow: inset 0 0 0 1px var(--accent);
    }

    .ticket-list span,
    .empty-state p,
    .empty,
    article small {
      color: var(--muted);
    }

    .messages {
      display: grid;
      gap: 12px;
      margin: 20px 0;
    }

    .messages article {
      background: color-mix(in srgb, var(--paper) 54%, transparent);
      border: 1px solid var(--border);
      border-radius: 22px;
      display: grid;
      gap: 8px;
      padding: 16px;
    }

    .messages article.mine {
      background: color-mix(in srgb, var(--accent) 15%, var(--paper));
    }

    .messages p {
      color: var(--ink);
      line-height: 1.55;
      margin: 0;
    }

    .reply-form {
      display: grid;
      gap: 12px;
    }

    textarea {
      background: color-mix(in srgb, var(--paper) 64%, transparent);
      border: 1px solid var(--border);
      border-radius: 22px;
      color: var(--ink);
      font: inherit;
      padding: 14px;
      resize: vertical;
    }

    button {
      background: var(--ink);
      border: 0;
      border-radius: 999px;
      color: var(--paper);
      cursor: pointer;
      font: inherit;
      font-weight: 950;
      padding: 13px 18px;
      width: max-content;
    }

    button:disabled {
      opacity: .5;
    }

    @media (max-width: 900px) {
      .ticket-layout {
        grid-template-columns: 1fr;
      }

      .ticket-list {
        position: static;
      }
    }
  `]
})
export class SupportTicketsPage {
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);
  private readonly support = inject(SupportService);
  private readonly seo = inject(SeoService);
  protected readonly tickets = signal<SupportTicket[]>([]);
  protected readonly details = signal<SupportTicketDetails | null>(null);
  protected readonly sending = signal(false);
  protected message = '';

  constructor() {
    this.route.paramMap.subscribe((params) => {
      void this.load(params.get('slug') ?? '');
    });
  }

  protected async sendMessage(ticket: SupportTicketDetails): Promise<void> {
    const text = this.message.trim();
    if (!text) {
      return;
    }
    this.sending.set(true);
    try {
      await this.support.sendMessage(ticket.id, text);
      this.message = '';
      await this.load(ticket.slug);
    } finally {
      this.sending.set(false);
    }
  }

  private async load(slug: string): Promise<void> {
    this.seo.setPage({
      title: slug ? 'Destek talebi' : 'Destek taleplerim',
      description: 'Destek taleplerini goruntule, gelen cevaplari takip et ve destek ekibine yanit ver.',
      path: slug ? this.i18n.routePath('supportTicketDetail', { slug }) : this.i18n.routePath('supportTickets')
    });
    try {
      this.tickets.set(await this.support.tickets());
    } catch {
      this.tickets.set([]);
      this.details.set(null);
      return;
    }
    if (slug) {
      try {
        const payload = await this.support.ticketDetails(slug);
        this.details.set(payload.ticketDetails);
      } catch {
        this.details.set(null);
      }
    } else {
      this.details.set(null);
    }
  }
}
